import { useState, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faCalendar,
  faUser,
  faCheckCircle,
  faFlag,
  faLeaf,
  faInfoCircle,
  faSpinner,
  faSearch,
  faEnvelope,
  faPhone,
  faBuilding,
  faMapMarkerAlt,
} from '@fortawesome/free-solid-svg-icons';
import Select from 'react-select';
import ClientService from '@frontend/AppSettings/services/ClientService';
import AgentsService from '@frontend/AppSettings/services/AgentsService';
import toastService from '@frontend/AppSettings/services/ToastService';
import { AddClientRequest } from '@frontend/Models/Client/ClientRequestResponseModels';
import { AddAgentRequest } from '@frontend/Models/Agent/AgentRequestResponseModels';
import { SelectOption } from '@frontend/Models/Form/FormModels';
import FormInput from '@frontend/Components/Forms/FormInput/FormInput';
import './CreateStakeholderModal.css';

interface CreateStakeholderModalProps {
  isOpen: boolean;
  onClose: () => void;
  stakeholderType: 'individual' | 'organization';
}

interface StakeholderFormData {
  ClientID: string;
  FirstName: string;
  LastName: string;
  Company: string;
  Email: string;
  Phone: string;
  Address: string;
  Jurisdiction?: string;
  Title?: string;
  stakeholderCategory?: 'client' | 'non-client';
}

const CreateStakeholderModal: React.FC<CreateStakeholderModalProps> = ({
  isOpen,
  onClose,
  stakeholderType,
}) => {
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState<StakeholderFormData>({
    ClientID: '',
    FirstName: '',
    LastName: '',
    Company: '',
    Email: '',
    Phone: '',
    Address: '',
    Jurisdiction: '',
    Title: '',
    stakeholderCategory: 'client',
  });

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  // Inline error specifically for the ClientID input so the user can fix the
  // duplicate without re-reading the toast.
  const [clientIdError, setClientIdError] = useState<string>('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name === 'ClientID' && clientIdError) {
      setClientIdError('');
    }
    setFormData({ ...formData, [name]: value });
  };

  const handlePhoneKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const key = e.key;
    if (['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(key)) {
      return;
    }
    if (!/^\d$/.test(key)) {
      e.preventDefault();
    }
  };

  const handleClose = useCallback(() => {
    setFormData({
      ClientID: '',
      FirstName: '',
      LastName: '',
      Company: '',
      Email: '',
      Phone: '',
      Address: '',
      Jurisdiction: '',
      Title: '',
      stakeholderCategory: 'client',
    });
    setError('');
    setClientIdError('');
    onClose();
  }, [onClose]);

  // A17: close on Escape (parity with the shared FormModal; this custom overlay had no keyboard dismiss).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [handleClose]);

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    setClientIdError('');

    // Validation
    if (!formData.FirstName || !formData.LastName) {
      setError('First name and last name are required.');
      setLoading(false);
      return;
    }

    if (!formData.Email) {
      setError('Email is required.');
      setLoading(false);
      return;
    }

    const customId = formData.ClientID?.trim() || undefined;

    if (customId && customId.length > 50) {
      setClientIdError('Client ID must be 50 characters or fewer.');
      setLoading(false);
      return;
    }

    const title = formData.Title?.trim() || '';
    const jurisdiction = formData.Jurisdiction?.trim() || '';

    try {
      if (stakeholderType === 'individual') {
        if (formData.stakeholderCategory === 'non-client') {
          // Non-client: create as agent via agent endpoint. Title and
          // Jurisdiction are both first-class columns on the agents table.
          const agentData: AddAgentRequest = {
            ...(customId && { AgentID: customId }),
            FirstName: formData.FirstName,
            LastName: formData.LastName,
            Company: formData.Company || '',
            Email: formData.Email,
            Phone: formData.Phone || '',
            Address: formData.Address || '',
            Jurisdiction: jurisdiction,
            Title: title,
          };
          await AgentsService.addAgent(agentData);
        } else {
          // Client: create as client via client endpoint. The clients table
          // has a Title column but no Jurisdiction column, so jurisdiction
          // is intentionally dropped here (and the modal warns the user).
          const clientData: AddClientRequest = {
            ...(customId && { ClientID: customId }),
            FirstName: formData.FirstName,
            LastName: formData.LastName,
            Company: formData.Company || '',
            Email: formData.Email,
            Phone: formData.Phone || '',
            Address: formData.Address || '',
            Title: title,
          };
          await ClientService.postClient(clientData);
        }
      } else {
        // Organization - create as client with company
        if (!formData.Company) {
          setError('Company name is required for organizations.');
          setLoading(false);
          return;
        }
        const clientData: AddClientRequest = {
          ...(customId && { ClientID: customId }),
          FirstName: formData.FirstName,
          LastName: formData.LastName,
          Company: formData.Company,
          Email: formData.Email,
          Phone: formData.Phone || '',
          Address: formData.Address || '',
          Title: title,
        };
        await ClientService.postClient(clientData);
      }

      // Invalidate queries to refresh data
      await queryClient.invalidateQueries({ queryKey: ['Clients', 'orbit'] });
      await queryClient.invalidateQueries({ queryKey: ['Agents', 'orbit'] });

      toastService.success(
        stakeholderType === 'individual'
          ? 'Individual created successfully.'
          : 'Organization created successfully.'
      );
      handleClose();
    } catch (error: any) {
      console.error('Error creating stakeholder:', error);
      const errorMessage = error?.message || 'Failed to create stakeholder. Please try again.';
      // 409 Conflict means the ClientID (or AgentID for non-clients) is
      // already taken — surface the message inline against the ID input so
      // the user can correct it without re-reading the toast.
      const isDuplicateIdError =
        error?.status === 409 ||
        /clientid|agentid|already exists/i.test(errorMessage);
      if (customId && isDuplicateIdError) {
        setClientIdError(errorMessage);
      } else {
        setError(errorMessage);
      }
      toastService.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="create-stakeholder-overlay" onClick={handleClose}>
      <div className="create-stakeholder-panel" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="create-stakeholder-header">
          <div className="create-stakeholder-header-left">
            <h2 className="create-stakeholder-title">
              Create New {stakeholderType === 'individual' ? 'Individual' : 'Organization'}
            </h2>
            <p className="create-stakeholder-subtitle">
              Add a new {stakeholderType === 'individual' ? 'individual' : 'organization'} to your network
            </p>
          </div>
          <button
            type="button"
            className="create-stakeholder-close"
            onClick={handleClose}
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Content */}
        <div className="create-stakeholder-content">
          {/* Stakeholder's Name / Organization Name Section */}
          <div className="create-stakeholder-section create-stakeholder-section-blue">
            <div className="create-stakeholder-section-header">
              <div className="create-stakeholder-section-icon-wrapper create-stakeholder-icon-blue">
                <FontAwesomeIcon icon={faInfoCircle} />
              </div>
              <h3 className="create-stakeholder-section-title">
                {stakeholderType === 'individual' ? "Stakeholder's Name" : 'Organization Name'}
              </h3>
            </div>
            <div className="create-stakeholder-section-content">
              <FormInput
                label="Client ID (optional)"
                name="ClientID"
                value={formData.ClientID}
                onChange={handleInputChange}
                placeholder="Leave blank for auto-generated ID (must be unique, max 50 chars)"
                maxLength={50}
                error={clientIdError || undefined}
              />
              {stakeholderType === 'individual' ? (
                <>
                  <FormInput
                    label="First Name"
                    name="FirstName"
                    value={formData.FirstName}
                    onChange={handleInputChange}
                    placeholder="Enter first name"
                    required
                  />
                  <FormInput
                    label="Last Name"
                    name="LastName"
                    value={formData.LastName}
                    onChange={handleInputChange}
                    placeholder="Enter last name"
                    required
                  />
                </>
              ) : (
                <div className="create-stakeholder-select-wrapper">
                  <FontAwesomeIcon icon={faBuilding} className="create-stakeholder-select-icon" />
                  <FormInput
                    label="Organization Name*"
                    name="Company"
                    value={formData.Company}
                    onChange={handleInputChange}
                    placeholder="Enter organization name"
                    required
                  />
                </div>
              )}
            </div>
          </div>

          {/* Stakeholder Category Section - Only for individuals */}
          {stakeholderType === 'individual' && (
            <div className="create-stakeholder-section create-stakeholder-section-purple">
              <div className="create-stakeholder-section-header">
                <div className="create-stakeholder-section-icon-wrapper create-stakeholder-icon-purple">
                  <FontAwesomeIcon icon={faUser} />
                </div>
                <h3 className="create-stakeholder-section-title">Stakeholder Category</h3>
              </div>
              <div className="create-stakeholder-section-content">
                <div className="create-stakeholder-radio-group">
                  <label className="create-stakeholder-radio-label">
                    <input
                      type="radio"
                      name="stakeholderCategory"
                      value="client"
                      checked={formData.stakeholderCategory === 'client'}
                      onChange={(e) => setFormData({ ...formData, stakeholderCategory: e.target.value as 'client' | 'non-client' })}
                      className="create-stakeholder-radio-input"
                    />
                    <span className="create-stakeholder-radio-text">Client</span>
                  </label>
                  <label className="create-stakeholder-radio-label">
                    <input
                      type="radio"
                      name="stakeholderCategory"
                      value="non-client"
                      checked={formData.stakeholderCategory === 'non-client'}
                      onChange={(e) => setFormData({ ...formData, stakeholderCategory: e.target.value as 'client' | 'non-client' })}
                      className="create-stakeholder-radio-input"
                    />
                    <span className="create-stakeholder-radio-text">Non-client (Outside Party/Vendor)</span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Role/title Section */}
          <div className="create-stakeholder-section create-stakeholder-section-purple">
            <div className="create-stakeholder-section-header">
              <div className="create-stakeholder-section-icon-wrapper create-stakeholder-icon-purple">
                <FontAwesomeIcon icon={faUser} />
              </div>
              <h3 className="create-stakeholder-section-title">Role/title</h3>
            </div>
            <div className="create-stakeholder-section-content">
              <FormInput
                label="Role/title"
                name="Title"
                value={formData.Title || ''}
                onChange={handleInputChange}
                placeholder="Enter role or title"
              />
              {stakeholderType === 'individual' && (
                <FormInput
                  label="Jurisdiction"
                  name="Jurisdiction"
                  value={formData.Jurisdiction || ''}
                  onChange={handleInputChange}
                  placeholder="Enter jurisdiction"
                />
              )}
              {stakeholderType === 'individual' && (
                <div className="create-stakeholder-info-box">
                  <FontAwesomeIcon icon={faInfoCircle} className="create-stakeholder-info-icon" />
                  <span>
                    {formData.stakeholderCategory === 'client'
                      ? 'Jurisdiction is not used when creating a client.'
                      : 'Jurisdiction can be provided for non-client (agent) stakeholders.'}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Primary Contact Section */}
          <div className="create-stakeholder-section create-stakeholder-section-green">
            <div className="create-stakeholder-section-header">
              <div className="create-stakeholder-section-icon-wrapper create-stakeholder-icon-green">
                <FontAwesomeIcon icon={faEnvelope} />
              </div>
              <h3 className="create-stakeholder-section-title">Primary Contact</h3>
            </div>
            <div className="create-stakeholder-section-content">
              {stakeholderType === 'organization' && (
                <>
                  <FormInput
                    label="First Name*"
                    name="FirstName"
                    value={formData.FirstName}
                    onChange={handleInputChange}
                    placeholder="Enter first name"
                    required
                  />
                  <FormInput
                    label="Last Name*"
                    name="LastName"
                    value={formData.LastName}
                    onChange={handleInputChange}
                    placeholder="Enter last name"
                    required
                  />
                </>
              )}
              <div className="create-stakeholder-input-wrapper">
                <FontAwesomeIcon icon={faEnvelope} className="create-stakeholder-input-icon" />
                <FormInput
                  label="Email*"
                  name="Email"
                  type="email"
                  value={formData.Email}
                  onChange={handleInputChange}
                  placeholder="Enter email address"
                  required
                />
              </div>
              <div className="create-stakeholder-input-wrapper">
                <FontAwesomeIcon icon={faPhone} className="create-stakeholder-input-icon" />
                <FormInput
                  label="Phone"
                  name="Phone"
                  type="tel"
                  value={formData.Phone}
                  onChange={handleInputChange}
                  onKeyDown={handlePhoneKeyDown}
                  placeholder="Enter phone number"
                />
              </div>
              <div className="create-stakeholder-input-wrapper">
                <FontAwesomeIcon icon={faMapMarkerAlt} className="create-stakeholder-input-icon" />
                <FormInput
                  label="Address"
                  name="Address"
                  value={formData.Address}
                  onChange={handleInputChange}
                  placeholder="Enter address"
                />
              </div>
            </div>
          </div>

          {/* Organization Section - Only show for individuals */}
          {stakeholderType === 'individual' && (
            <div className="create-stakeholder-section create-stakeholder-section-blue">
              <div className="create-stakeholder-section-header">
                <div className="create-stakeholder-section-icon-wrapper create-stakeholder-icon-blue">
                  <FontAwesomeIcon icon={faBuilding} />
                </div>
                <h3 className="create-stakeholder-section-title">Organization</h3>
              </div>
              <div className="create-stakeholder-section-content">
                <div className="create-stakeholder-select-wrapper">
                  <FontAwesomeIcon icon={faBuilding} className="create-stakeholder-select-icon" />
                  <FormInput
                    label="Organization (Optional)"
                    name="Company"
                    value={formData.Company}
                    onChange={handleInputChange}
                    placeholder="Enter organization"
                  />
                </div>
              </div>
            </div>
          )}

          {error && <div className="create-stakeholder-error">{error}</div>}
        </div>

        {/* Footer Actions */}
        <div className="create-stakeholder-footer">
          <div className="create-stakeholder-footer-info">
            <FontAwesomeIcon icon={faInfoCircle} className="create-stakeholder-footer-info-icon" />
            <span>All required fields must be completed</span>
          </div>
          <div className="create-stakeholder-footer-buttons">
            <button
              type="button"
              className="create-stakeholder-button create-stakeholder-button-cancel"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="create-stakeholder-button create-stakeholder-button-primary"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin />
                  Creating...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faCheckCircle} />
                  Create {stakeholderType === 'individual' ? 'Individual' : 'Organization'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateStakeholderModal;

